import React from "react";
import { useDispatch } from "react-redux";
import { startCase } from "lodash";
import { Box, Typography } from "@mui/material";
import { notificationAdd } from "../../actions/notification";
import { MODULE, errorFetchMessage } from "../../helpers/messages";
import Filters, { Entity, FilterProps } from "./Filters";
import { CalendarFunctions } from "../../components/Calendar";
import { getHistoryReportData } from "../../api/reports";
import useMobileScreen from "../../hooks/useMobileScreen";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { convertADtoBS } from "../../components/Calendar/functions/calendarFunctions";
import { History } from "../../interfaces/HistoryInterface";
import ClientInfo from "../Client/ClientInfo/ClientInfo";
import useCan from "../../hooks/useCan";
import { TypeOfCustomer, VendorType } from "../../interfaces/Accounts";
import { downloadCSV } from "../../helpers/files";
import { t, tl } from "../../components/translate";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import "./Reports.scss";

const entityOptions = [
  ...Object.values(Entity).filter((item) => item !== Entity.RESOURCE_CENTRE),
  TypeOfCustomer.CLIENT,
  VendorType.SERVICE_PROVIDER,
  VendorType.EMPLOYEE
];

const docColumns = () => ["Module", "Entity Id", "Action", "Created By", "Date", "Client Id"];

// eslint-disable-next-line camelcase
const docRowProcessor = ({ entity, entityId, event, created_at, username, clientId }): unknown => [
  entity,
  entityId,
  event,
  username,
  // eslint-disable-next-line camelcase
  created_at,
  clientId
];

export default function HistoryReport(): React.ReactNode {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const isSuperAdmin = useCan("deny", { superAdminPass: true, supportAccountAdminPass: true });

  const [historyData, setHistoryData] = React.useState<History[]>([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState<number | null>(null);
  const [filters, setFilters] = React.useState<FilterProps>({
    from: CalendarFunctions.startOfDay(new Date()).toDate(),
    until: new Date(),
    entity: Entity.BILL
  });

  const handleClose = () => setSelectedClientId(null);

  React.useEffect(() => {
    const getHistory = async () => {
      try {
        setIsRequestLoading(true);
        const history = await getHistoryReportData(filters);
        setHistoryData(history);
      } catch (error: any) {
        dispatch(
          notificationAdd({
            id: new Date().getTime(),
            message: error?.data?.message || errorFetchMessage(MODULE.HISTORY),
            variant: "error",
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    };
    getHistory();
  }, [filters, dispatch]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* filter */}
      <Box sx={{ px: 3, py: 2 }}>
        <Filters
          filters={filters}
          onSetFilters={(updatedFilters) => setFilters(updatedFilters)}
          entityOptions={[...entityOptions, ...(isSuperAdmin ? [Entity.RESOURCE_CENTRE] : [])]}
          isRequestLoading={isRequestLoading}
        />
      </Box>

      {/* list */}
      <Box
        className="historyReportList"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
          overflow: "auto"
        }}
      >
        <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "1100px" : "auto" }}>
          <List
            automation="historyReport"
            data={historyData}
            rowHeight={50}
            defaultSortOrder={-1}
            activeRow={1}
            adjustHeightToContents
            columns={[
              {
                key: "id",
                label: "Entity Id",
                sortable: true,
                formatter: ({ entityId }) => <Typography>{entityId}</Typography>
              },
              {
                key: "entity",
                label: "Module",
                sortable: true,
                formatter: ({ entity }) => <Typography>{startCase(entity)}</Typography>
              },
              {
                key: "event",
                label: "Action",
                sortable: true,
                formatter: ({ event }) => <Typography>{startCase(event)}</Typography>
              },
              {
                key: "username",
                label: "Created By",
                sortable: true,
                formatter: ({ username }) => (
                  // Previously if the activity done by super admin the null null value was set.
                  <Typography>{username === "null null" ? "" : startCase(username)}</Typography>
                )
              },
              {
                key: "created_at",
                label: "Date",
                sortable: true,
                // eslint-disable-next-line camelcase
                formatter: ({ created_at }) => (
                  <Typography>{convertADtoBS(new Date(created_at)).formatted4}</Typography>
                )
              },
              {
                key: "clientId",
                label: "Client ID",
                sortable: true,
                formatter: ({ clientId }) => (
                  <Typography
                    onClick={() => setSelectedClientId(clientId)}
                    sx={{ textDecoration: "underline" }}
                    color="primary"
                  >
                    {clientId}
                  </Typography>
                )
              },
              ...(isSuperAdmin
                ? [
                    {
                      key: "resourceCentreId",
                      label: "Resource Centre Id",
                      sortable: true,
                      formatter: ({ resourceCentreId }) => (
                        <Typography>{resourceCentreId}</Typography>
                      )
                    }
                  ]
                : [])
            ]}
          >
            <EmptyView>
              <Box textAlign="center" padding="50px">
                No items to show.
              </Box>
            </EmptyView>
            <ListActions>
              {({ getProcessedData }) => (
                <Menu>
                  <MenuItem
                    onClick={() =>
                      downloadCSV(
                        t("reports.history", "en"),
                        getProcessedData(),
                        docColumns(),
                        (row) => docRowProcessor(row),
                        rc,
                        { from: filters.from, until: filters.until }
                      )
                    }
                  >
                    {tl("reports.excel")}
                  </MenuItem>
                </Menu>
              )}
            </ListActions>
          </List>
        </Box>
      </Box>

      {/* panels and dialogs */}
      <>{selectedClientId && <ClientInfo id={selectedClientId} handleViewClose={handleClose} />}</>
    </Box>
  );
}
